import { FlagWarningIcon, XBlackIcon } from '@assets/iconComponents';
import { Button, Dialog, Paper, PaperProps, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { Text, Textarea } from '..';
import { colorSet } from '@theme';
import { useToast } from '@hooks';
import { useFetchImageIssueReportList, usePostIssueReport } from '@api';
import { ToastMessageEnum } from '@constant';

interface IIssueReportModalProps {
  isVisible: boolean;
  onClose: () => void;
  imageId?: number;
}

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const IssueReportList = styled('div')({
  width: '100%',
  maxHeight: 'calc(100vh - 628px)',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  },

  '&.show-scrollbar': {
    paddingRight: '6px'
  }
});

const PaperComponent = (props: PaperProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
};

export const IssueReportModal = (props: IIssueReportModalProps) => {
  const { isVisible, onClose, imageId } = props;

  const [reportText, setReportText] = useState<string>('');
  // 미해결 리포트 리스트의 스크롤바 여부에 따른 CSS 분기를 위한 상태
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const { showToast } = useToast();

  const issueReportListRef = useRef<HTMLDivElement>(null);

  const { mutateAsync, isPending: isPostReportPending } = usePostIssueReport();

  const { data: issueReportListData } = useFetchImageIssueReportList(isVisible ? imageId : undefined);

  const handleReportTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    if (input.length <= 600) {
      setReportText(input);
    } else {
      setReportText(input.slice(0, 600)); // 한글 입력시 601번째 글자 입력되는 현상 방지
    }
  };

  const handleReportSubmit = () => {
    if (imageId && reportText.trim().length > 0) {
      mutateAsync({ imageId: imageId, reportContent: reportText.trim() }).then(() => {
        onClose();
        showToast(ToastMessageEnum.REPORT_SUCCESS);
      });
    }
  };

  useEffect(() => {
    if (!isVisible) {
      setReportText('');
    }
  }, [isVisible]);

  // 미해결 리포스 리스트의 스크롤바 존재 여부 체크
  useEffect(() => {
    const checkScrollbar = () => {
      if (issueReportListRef.current) {
        const element = issueReportListRef.current;
        const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };

    // 초기 체크를 약간 지연시켜 DOM이 완전히 렌더링된 후 실행
    const timeoutId = setTimeout(checkScrollbar, 100);

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', checkScrollbar);

    // MutationObserver로 DOM 변화 감지
    const observer = new MutationObserver(checkScrollbar);
    if (issueReportListRef.current) {
      observer.observe(issueReportListRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollbar);
      observer.disconnect();
    };
  }, [isVisible]);

  return (
    <Dialog
      open={isVisible}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent'
        },
        '& .MuiDialog-paper': {
          width: 512,
          minHeight: 592,
          maxHeight: 'calc(100% - 80px)',
          //padding: '60px 48px 48px 48px', //draggable header 적용 위해 content frame에 padding 부여
          boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
          borderRadius: 0
        }
      }}
    >
      <div id="draggable-dialog-title" style={{ width: '100%', height: '60px', cursor: 'move' }} />
      <XBlackIcon
        style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
        onClick={onClose}
      />

      <div
        style={{
          padding: '0px 48px 48px 48px',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          <FlagWarningIcon style={{ width: 60, height: 60 }} />
          <Text variant="h3" fontWeight="bold" color={colorSet.gray500}>
            오류 리포트
          </Text>
          <Text variant="subtitle">라벨링 정보에서 잘못되었거나 불편했던 점을 알려주세요.</Text>
          <Textarea
            placeholder="오류를 입력해주세요."
            value={reportText}
            onChange={handleReportTextChange}
            maxLength={600}
          />
          <Button
            disabled={reportText.trim().length === 0 || isPostReportPending}
            onClick={handleReportSubmit}
            variant="contained"
            color="primary"
            sx={{ width: '100%' }}
          >
            제출
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center'
          }}
        >
          <Text variant="subtitle" color={colorSet.gray500}>
            이미 같은 오류가 리포트된 경우, 중복 접수는 피해주세요.
          </Text>
          <IssueReportList ref={issueReportListRef} className={hasScrollbar ? 'show-scrollbar' : ''}>
            <div style={{ backgroundColor: colorSet.gray100 }}>
              {issueReportListData && issueReportListData.length > 0 ? (
                issueReportListData.map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        borderRadius: '10px',
                        borderBottom: `1px solid ${colorSet.gray200}`
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        {item.reportContent}
                      </Text>
                      <Text variant="body2" color={colorSet.gray400}>
                        {item.createdAt}
                      </Text>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 44,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Text variant="body1" color={colorSet.gray400}>
                    아직 작성된 리포트가 없습니다.
                  </Text>
                </div>
              )}
            </div>
          </IssueReportList>
        </div>
      </div>
    </Dialog>
  );
};
